import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, Image as ImageIcon, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/context/I18nContext";
import { EmailTemplates } from "@/components/EmailTemplates";

export default function Settings() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);
  const websiteLogoRef = useRef(null);
  const compactLogoRef = useRef(null);
  const faviconRef = useRef(null);

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  useEffect(() => { api.get("/admin/email-status").then(({ data }) => setEmailStatus(data)).catch((e) => logError("settings:email-status", e)); }, []);

  const save = async () => {
    try { const { data } = await api.put("/settings", { data: s }); setS(data); toast.success(t("toast.saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) { toast.error(t("toast.emailDestRequired")); return; }
    try {
      const { data } = await api.post("/admin/email/test", { to: testEmail.trim() });
      if (data.status === "sent") toast.success(t("toast.emailSent"));
      else if (data.status === "mocked") toast.info(t("toast.emailMocked"));
      else toast.error(`${data.error || data.status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (e, kind = "compact") => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fieldMap = { website: "website_logo_document_id", compact: "compact_logo_document_id", favicon: "favicon_document_id" };
    const categoryMap = { website: "website_logo", compact: "compact_logo", favicon: "favicon" };
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", "branding");
    fd.append("entity_id", "logo"); fd.append("category", categoryMap[kind]);
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = structuredClone(s);
      (next.pdf_settings ||= {})[fieldMap[kind]] = data.id;
      if (kind === "compact") next.pdf_settings.logo_document_id = data.id; // PDFs use compact logo
      await api.put("/settings", { data: { pdf_settings: next.pdf_settings } });
      setS(next);
      toast.success(t("toast.logoSaved"));
      if (kind === "compact" || kind === "website") {
        try {
          const { data: ic } = await api.post("/admin/branding/regenerate-icons");
          if (ic?.variants?.favicon_32) {
            const n2 = structuredClone(next);
            n2.pdf_settings.favicon_document_id = ic.variants.favicon_32;
            n2.pdf_settings.logo_variants = ic.variants;
            await api.put("/settings", { data: { pdf_settings: n2.pdf_settings } });
            setS(n2);
          }
          toast.success(t("toast.iconsGenerated"));
        } catch (e) { logError("settings:regenerate-icons", e); /* non-blocking: logo already saved */ }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const regenerateIcons = async () => {
    try {
      const { data } = await api.post("/admin/branding/regenerate-icons");
      const next = structuredClone(s);
      next.pdf_settings.favicon_document_id = data.variants.favicon_32;
      next.pdf_settings.logo_variants = data.variants;
      await api.put("/settings", { data: { pdf_settings: next.pdf_settings } });
      setS(next);
      toast.success(t("toast.iconsGenerated"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const testConnection = async () => {
    try {
      await api.put("/settings", { data: s });
      const { data } = await api.post("/admin/email/test-connection");
      if (data.status === "ok") toast.success(t("email.connOk"));
      else toast.error(`${data.error || "error"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!s) return <div className="text-slate-400 py-10">{t("common.loading")}</div>;

  const setField = (path, value) => {
    const next = structuredClone(s);
    let o = next; const keys = path.split(".");
    for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
    o[keys[keys.length - 1]] = value;
    setS(next);
  };
  const updateArr = (key, idx, field, value) => {
    const next = structuredClone(s); next[key][idx][field] = value; setS(next);
  };
  const removeArr = (key, idx) => { const next = structuredClone(s); next[key].splice(idx, 1); setS(next); };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("set.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("set.subtitle")}</p>
        </div>
        <Button onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="settings-save-btn"><Save size={16} className="mr-1.5" /> {t("common.save")}</Button>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="tab-general">{t("set.t.company")}</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">{t("set.t.branding")}</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">{t("set.t.services")}</TabsTrigger>
          <TabsTrigger value="transport" data-testid="tab-transport">{t("set.t.transport")}</TabsTrigger>
          <TabsTrigger value="wtransport" data-testid="tab-wtransport">{t("set.t.wtransport")}</TabsTrigger>
          <TabsTrigger value="wservices" data-testid="tab-wservices">{t("set.t.wservices")}</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">{t("set.t.email")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">{t("pay.tab")}</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">{t("set.t.seo")}</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">{t("set.t.legal")}</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags">{t("set.t.flags")}</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card title={t("set.c.company")}>
            <div className="grid md:grid-cols-2 gap-4">
              {["name", "legal_name", "email", "phone", "emergency_phone", "address", "postal_code", "city", "country", "vat_number", "tax_code", "iban", "swift", "website", "copyright_text"].map((k) => (
                <div key={k} className="space-y-1.5"><Label className="capitalize">{k.replace(/_/g, " ")}</Label>
                  <Input value={s.company?.[k] || ""} onChange={(e) => setField(`company.${k}`, e.target.value)} data-testid={`company-${k}`} /></div>
              ))}
            </div>
          </Card>
          <Card title={t("set.c.taxes")}>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" value={s.tax?.vat_pct ?? 0} onChange={(e) => setField("tax.vat_pct", Number(e.target.value))} data-testid="tax-vat" /></div>
              <div className="space-y-1.5"><Label>Commissione partner %</Label><Input type="number" value={s.commissions?.partner_default_pct ?? 0} onChange={(e) => setField("commissions.partner_default_pct", Number(e.target.value))} data-testid="commission-pct" /></div>
              <div className="space-y-1.5"><Label>Valuta</Label><Input value={s.currency || "EUR"} onChange={(e) => setField("currency", e.target.value)} data-testid="currency" /></div>
              <div className="space-y-1.5">
                <Label>Moltiplicatore andata/ritorno</Label>
                <Input type="number" step="0.1" value={s.pricing_rules?.return_multiplier ?? 1} onChange={(e) => setField("pricing_rules.return_multiplier", Number(e.target.value))} data-testid="return-multiplier" />
                <p className="text-xs text-slate-400">Km fatturati = distanza stradale × questo valore (es. 2.0 = andata e ritorno).</p>
              </div>
              <div className="space-y-1.5 rounded-lg border-2 border-red-500 bg-red-50 p-3">
                <Label className="text-red-700 font-semibold">Ricarico / margine (%) — il tuo profitto</Label>
                <Input type="number" step="0.1" value={s.pricing_rules?.markup_pct ?? 0} onChange={(e) => setField("pricing_rules.markup_pct", Number(e.target.value))} data-testid="markup-pct" className="border-red-400 focus-visible:ring-red-500 bg-white" />
                <p className="text-xs text-red-600">Percentuale aggiunta sul subtotale (prima dell'IVA). Il cliente non la vede: viene inclusa nel prezzo del trasporto. Vale per tutti i preventivi.</p>
              </div>
            </div>
          </Card>
          <Card title={t("mp.title")}>
            <p className="text-sm text-slate-500 mb-4">{t("mp.sub")}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{t("mp.platformMargin")}</Label>
                <Input type="number" step="0.1" value={s.pricing_rules?.platform_margin_pct ?? 20} onChange={(e) => setField("pricing_rules.platform_margin_pct", Number(e.target.value))} data-testid="platform-margin-pct" />
                <p className="text-xs text-slate-400">{t("mp.platformMarginHint")}</p></div>
              <div className="space-y-1.5"><Label>{t("mp.minMargin")}</Label>
                <Input type="number" step="0.1" value={s.pricing_rules?.min_margin_pct ?? 15} onChange={(e) => setField("pricing_rules.min_margin_pct", Number(e.target.value))} data-testid="min-margin-pct" />
                <p className="text-xs text-slate-400">{t("mp.minMarginHint")}</p></div>
              <div className="space-y-1.5"><Label>{t("mp.goodMargin")}</Label>
                <Input type="number" step="0.1" value={s.pricing_rules?.good_margin_pct ?? 25} onChange={(e) => setField("pricing_rules.good_margin_pct", Number(e.target.value))} data-testid="good-margin-pct" />
                <p className="text-xs text-slate-400">{t("mp.goodMarginHint")}</p></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("mp.stripePct")}</Label>
                  <Input type="number" step="0.01" value={s.pricing_rules?.stripe_fee_pct ?? 1.8} onChange={(e) => setField("pricing_rules.stripe_fee_pct", Number(e.target.value))} data-testid="stripe-fee-pct" /></div>
                <div className="space-y-1.5"><Label>{t("mp.stripeFixed")}</Label>
                  <Input type="number" step="0.01" value={s.pricing_rules?.stripe_fee_fixed ?? 0.25} onChange={(e) => setField("pricing_rules.stripe_fee_fixed", Number(e.target.value))} data-testid="stripe-fee-fixed" /></div>
              </div>
            </div>
            <Button onClick={save} className="mt-5 bg-blue-900 hover:bg-blue-800" data-testid="profit-settings-save">{t("common.save")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="space-y-6">
          <Card title={t("set.c.logos")}>
            <p className="text-sm text-slate-500 mb-4">Il <b>Logo Sito</b> appare su homepage, sito pubblico e portale clienti. Il <b>Logo Compatto</b> appare nel pannello admin, PDF, fatture, preventivi e POD. Modifiche immediate ovunque.</p>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { kind: "website", label: "Logo Sito", field: "website_logo_document_id", ref: websiteLogoRef, tid: "website" },
                { kind: "compact", label: "Logo Compatto", field: "compact_logo_document_id", ref: compactLogoRef, tid: "compact" },
                { kind: "favicon", label: "Favicon", field: "favicon_document_id", ref: faviconRef, tid: "favicon" },
              ].map((slot) => (
                <div key={slot.kind} className="space-y-2">
                  <Label>{slot.label}</Label>
                  <div className="h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden" data-testid={`logo-preview-${slot.tid}`}>
                    {s.pdf_settings?.[slot.field]
                      ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/branding-asset/${s.pdf_settings[slot.field]}`} alt={slot.label} className="max-h-full max-w-full object-contain p-2" />
                      : <ImageIcon className="text-slate-300" size={28} />}
                  </div>
                  <input ref={slot.ref} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e, slot.kind)} data-testid={`logo-input-${slot.tid}`} />
                  <Button variant="outline" size="sm" className="w-full" onClick={() => slot.ref.current?.click()} data-testid={`logo-upload-${slot.tid}`}><Upload size={14} className="mr-1.5" /> {t("common.upload")}</Button>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={regenerateIcons} data-testid="regen-icons-btn"><ImageIcon size={14} className="mr-1.5" /> {t("set.regenIcons")}</Button>
              <p className="text-xs text-slate-400 mt-2">Favicon (16/32), Apple Touch (180), Android (192/512).</p>
            </div>
          </Card>
          <Card title={t("set.c.pdfTexts")}>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Testo di intestazione (header)</Label>
                <Input value={s.pdf_settings?.header_text || ""} onChange={(e) => setField("pdf_settings.header_text", e.target.value)} data-testid="pdf-header-text" /></div>
              <div className="space-y-1.5"><Label>Testo a piè di pagina (footer)</Label>
                <Input value={s.pdf_settings?.footer_text || ""} onChange={(e) => setField("pdf_settings.footer_text", e.target.value)} data-testid="pdf-footer-text" /></div>
              <div className="space-y-2">
                <Label>Termini e condizioni (per lingua)</Label>
                {["it", "ro", "en"].map((loc) => (
                  <Textarea key={loc} rows={2} placeholder={loc.toUpperCase()}
                    value={(typeof s.pdf_settings?.terms_text === "object" ? s.pdf_settings?.terms_text?.[loc] : (loc === "it" ? s.pdf_settings?.terms_text : "")) || ""}
                    onChange={(e) => {
                      const n = structuredClone(s);
                      if (typeof n.pdf_settings.terms_text !== "object" || n.pdf_settings.terms_text === null) n.pdf_settings.terms_text = { it: "", ro: "", en: "" };
                      n.pdf_settings.terms_text[loc] = e.target.value; setS(n);
                    }} data-testid={`pdf-terms-${loc}`} />
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="services">
          <Card title={t("set.c.services")} action={() => { const n = structuredClone(s); n.services.push({ code: "new_service", name: { it: "", ro: "", en: "" }, base_price: 0, active: true }); setS(n); }}>
            <div className="space-y-3">
              {s.services?.map((sv, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="service-row">
                  <Input className="col-span-3" placeholder="codice" value={sv.code} onChange={(e) => updateArr("services", i, "code", e.target.value)} />
                  <Input className="col-span-4" placeholder="Nome IT" value={sv.name?.it || ""} onChange={(e) => { const n = structuredClone(s); n.services[i].name.it = e.target.value; setS(n); }} />
                  <Input className="col-span-2" type="number" placeholder="Prezzo" value={sv.base_price} onChange={(e) => updateArr("services", i, "base_price", Number(e.target.value))} />
                  <div className="col-span-2 flex items-center gap-2"><Switch checked={sv.active} onCheckedChange={(v) => updateArr("services", i, "active", v)} /><span className="text-xs text-slate-400">attivo</span></div>
                  <Button size="icon" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeArr("services", i)}><Trash2 size={15} /></Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="transport">
          <Card title={t("set.c.transportTypes")} action={() => { const n = structuredClone(s); n.transport_types.push({ code: "new_type", name: { it: "", ro: "", en: "" }, price_per_km: 0, active: true }); setS(n); }}>
            <div className="space-y-3">
              {s.transport_types?.map((tr, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="transport-row">
                  <Input className="col-span-3" placeholder="codice" value={tr.code} onChange={(e) => updateArr("transport_types", i, "code", e.target.value)} />
                  <Input className="col-span-4" placeholder="Nome IT" value={tr.name?.it || ""} onChange={(e) => { const n = structuredClone(s); n.transport_types[i].name.it = e.target.value; setS(n); }} />
                  <Input className="col-span-2" type="number" placeholder="€/km" value={tr.price_per_km} onChange={(e) => updateArr("transport_types", i, "price_per_km", Number(e.target.value))} />
                  <div className="col-span-2 flex items-center gap-2"><Switch checked={tr.active} onCheckedChange={(v) => updateArr("transport_types", i, "active", v)} /><span className="text-xs text-slate-400">attivo</span></div>
                  <Button size="icon" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeArr("transport_types", i)}><Trash2 size={15} /></Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="wtransport">
          <Card title={t("set.c.wizTransport")} action={() => { const n = structuredClone(s); (n.wizard_transport ||= []).push({ code: "new", name: { it: "", ro: "", en: "" }, desc: { it: "", ro: "", en: "" }, base_price: 0, price_per_km: 0, active: true }); setS(n); }}>
            <div className="grid grid-cols-12 gap-2 px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <span className="col-span-2">Codice</span><span className="col-span-3">Nome IT</span><span className="col-span-2">Base €</span><span className="col-span-2">€/km</span><span className="col-span-2">Attivo</span><span className="col-span-1" />
            </div>
            <div className="space-y-3">
              {(s.wizard_transport || []).map((tr, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="wtransport-row">
                  <Input className="col-span-2" placeholder="codice" value={tr.code} onChange={(e) => updateArr("wizard_transport", i, "code", e.target.value)} />
                  <Input className="col-span-3" placeholder="Nome IT" value={tr.name?.it || ""} onChange={(e) => { const n = structuredClone(s); n.wizard_transport[i].name.it = e.target.value; setS(n); }} />
                  <Input className="col-span-2" type="number" placeholder="Base" value={tr.base_price} onChange={(e) => updateArr("wizard_transport", i, "base_price", Number(e.target.value))} />
                  <Input className="col-span-2" type="number" placeholder="€/km" value={tr.price_per_km} onChange={(e) => updateArr("wizard_transport", i, "price_per_km", Number(e.target.value))} />
                  <div className="col-span-2 flex items-center gap-2"><Switch checked={tr.active} onCheckedChange={(v) => updateArr("wizard_transport", i, "active", v)} /><span className="text-xs text-slate-400">attivo</span></div>
                  <Button size="icon" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeArr("wizard_transport", i)}><Trash2 size={15} /></Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="wservices">
          <Card title={t("set.c.wizServices")} action={() => { const n = structuredClone(s); (n.wizard_services ||= []).push({ code: "new", name: { it: "", ro: "", en: "" }, price: 0, active: true }); setS(n); }}>
            <div className="grid grid-cols-12 gap-2 px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <span className="col-span-3">Codice</span><span className="col-span-4">Nome IT</span><span className="col-span-2">Prezzo €</span><span className="col-span-1">Obbl.</span><span className="col-span-1">On</span><span className="col-span-1" />
            </div>
            <div className="space-y-3">
              {(s.wizard_services || []).map((sv, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="wservice-row">
                  <Input className="col-span-3" placeholder="codice" value={sv.code} onChange={(e) => updateArr("wizard_services", i, "code", e.target.value)} />
                  <Input className="col-span-4" placeholder="Nome IT" value={sv.name?.it || ""} onChange={(e) => { const n = structuredClone(s); n.wizard_services[i].name.it = e.target.value; setS(n); }} />
                  <Input className="col-span-2" type="number" placeholder="Prezzo" value={sv.price} onChange={(e) => updateArr("wizard_services", i, "price", Number(e.target.value))} />
                  <div className="col-span-1" title="Obbligatorio (aggiunto automaticamente, il cliente non può toglierlo)"><Switch checked={!!sv.mandatory} onCheckedChange={(v) => updateArr("wizard_services", i, "mandatory", v)} data-testid={`wservice-mandatory-${i}`} /></div>
                  <div className="col-span-1"><Switch checked={sv.active} onCheckedChange={(v) => updateArr("wizard_services", i, "active", v)} /></div>
                  <Button size="icon" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeArr("wizard_services", i)}><Trash2 size={15} /></Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card title={t("set.c.email")}>
            {emailStatus && !emailStatus.configured && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="email-mock-warning">
                ⚠ {emailStatus.warning}
              </div>
            )}
            <div className="flex items-center gap-3 mb-5">
              <Label className="font-medium">{t("email.provider")}</Label>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="email-provider-toggle">
                {["resend", "smtp"].map((p) => (
                  <button key={p} onClick={() => setField("email_settings.provider", p)}
                    className={`px-4 py-1.5 text-sm ${(s.email_settings?.provider || "resend") === p ? "bg-blue-900 text-white" : "bg-white text-slate-600"}`}
                    data-testid={`email-provider-${p}`}>{p === "resend" ? "Resend" : "SMTP"}</button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{t("email.senderName")}</Label>
                <Input value={s.email_settings?.sender_name || ""} onChange={(e) => setField("email_settings.sender_name", e.target.value)} data-testid="email-sender-name" /></div>
              <div className="space-y-1.5"><Label>{t("email.senderEmail")}</Label>
                <Input value={s.email_settings?.sender_email || ""} onChange={(e) => setField("email_settings.sender_email", e.target.value)} data-testid="email-sender-email" /></div>
              <div className="space-y-1.5"><Label>{t("email.replyTo")}</Label>
                <Input value={s.email_settings?.reply_to || ""} onChange={(e) => setField("email_settings.reply_to", e.target.value)} data-testid="email-reply-to" /></div>

              {(s.email_settings?.provider || "resend") === "resend" ? (
                <div className="space-y-1.5"><Label>Resend API Key</Label>
                  <Input type="password" placeholder="re_..." value={s.email_settings?.resend_api_key || ""} onChange={(e) => setField("email_settings.resend_api_key", e.target.value)} data-testid="email-resend-key" /></div>
              ) : (
                <>
                  <div className="space-y-1.5"><Label>{t("email.smtpHost")}</Label>
                    <Input value={s.email_settings?.smtp_host || ""} onChange={(e) => setField("email_settings.smtp_host", e.target.value)} data-testid="email-smtp-host" /></div>
                  <div className="space-y-1.5"><Label>{t("email.smtpPort")}</Label>
                    <Input type="number" placeholder="587" value={s.email_settings?.smtp_port || ""} onChange={(e) => setField("email_settings.smtp_port", e.target.value)} data-testid="email-smtp-port" /></div>
                  <div className="space-y-1.5"><Label>{t("email.smtpUser")}</Label>
                    <Input value={s.email_settings?.smtp_username || ""} onChange={(e) => setField("email_settings.smtp_username", e.target.value)} data-testid="email-smtp-user" /></div>
                  <div className="space-y-1.5"><Label>{t("email.smtpPass")}</Label>
                    <Input type="password" value={s.email_settings?.smtp_password || ""} onChange={(e) => setField("email_settings.smtp_password", e.target.value)} data-testid="email-smtp-pass" /></div>
                  <div className="space-y-1.5"><Label>{t("email.encryption")}</Label>
                    <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={s.email_settings?.smtp_encryption || "tls"} onChange={(e) => setField("email_settings.smtp_encryption", e.target.value)} data-testid="email-smtp-encryption">
                      <option value="none">None</option><option value="ssl">SSL</option><option value="tls">TLS</option>
                    </select></div>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 mt-5 flex-wrap">
              <Input className="max-w-xs" placeholder="email@destinatario.it" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="email-test-input" />
              <Button variant="outline" onClick={sendTestEmail} data-testid="email-test-btn"><Send size={15} className="mr-1.5" /> {t("email.sendTest")}</Button>
              {(s.email_settings?.provider || "resend") === "smtp" && (
                <Button variant="outline" onClick={testConnection} data-testid="email-test-conn-btn">{t("email.testConn")}</Button>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">{t("email.saveFirst")}</p>
          </Card>
          <Card title={t("mail.title")}>
            <EmailTemplates />
          </Card>
          <Card title={t("mand.set.title")}>
            <p className="text-sm text-slate-500 mb-4">{t("mand.set.sub")}</p>
            <div className="space-y-4">
              <label className="flex items-center justify-between gap-4 cursor-pointer">
                <span className="text-sm"><b>{t("mand.set.otpRequired")}</b><br /><span className="text-xs text-slate-400">{t("mand.set.otpRequiredHint")}</span></span>
                <Switch checked={!!s.mandate_settings?.otp_required} onCheckedChange={(v) => setField("mandate_settings.otp_required", v)} data-testid="mandate-otp-required" />
              </label>
              <label className="flex items-center justify-between gap-4 cursor-pointer">
                <span className="text-sm"><b>{t("mand.set.otpSkipIfPaid")}</b><br /><span className="text-xs text-slate-400">{t("mand.set.otpSkipIfPaidHint")}</span></span>
                <Switch checked={s.mandate_settings?.otp_skip_if_paid !== false} onCheckedChange={(v) => setField("mandate_settings.otp_skip_if_paid", v)} data-testid="mandate-otp-skip" />
              </label>
              <label className="flex items-center justify-between gap-4 cursor-pointer">
                <span className="text-sm"><b>{t("mand.set.driverVisibility")}</b><br /><span className="text-xs text-slate-400">{t("mand.set.driverVisibilityHint")}</span></span>
                <Switch checked={!!s.mandate_settings?.driver_visibility} onCheckedChange={(v) => setField("mandate_settings.driver_visibility", v)} data-testid="mandate-driver-visibility" />
              </label>
              <div className="border-t border-slate-100 pt-4 space-y-4">
                <label className="flex items-center justify-between gap-4 cursor-pointer">
                  <span className="text-sm"><b>{t("mand.set.reminderEnabled")}</b><br /><span className="text-xs text-slate-400">{t("mand.set.reminderEnabledHint")}</span></span>
                  <Switch checked={s.mandate_settings?.reminder_enabled !== false} onCheckedChange={(v) => setField("mandate_settings.reminder_enabled", v)} data-testid="mandate-reminder-enabled" />
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("mand.set.reminderInterval")}</Label>
                    <Input type="number" min="1" value={s.mandate_settings?.reminder_interval_hours ?? 6}
                      onChange={(e) => setField("mandate_settings.reminder_interval_hours", Number(e.target.value))}
                      data-testid="mandate-reminder-interval" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("mand.set.reminderMax")}</Label>
                    <Input type="number" min="0" value={s.mandate_settings?.reminder_max ?? 3}
                      onChange={(e) => setField("mandate_settings.reminder_max", Number(e.target.value))}
                      data-testid="mandate-reminder-max" />
                  </div>
                </div>
                <p className="text-xs text-slate-400">{t("mand.set.reminderNote")}</p>
              </div>
            </div>
            <Button onClick={save} className="mt-5 bg-blue-900 hover:bg-blue-800" data-testid="mandate-settings-save">{t("common.save")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card title={t("pay.title")}>
            <div className="flex items-center gap-3 mb-5">
              <Label className="font-medium">{t("pay.mode")}</Label>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="payment-mode-toggle">
                {["test", "live"].map((m) => (
                  <button key={m} onClick={() => setField("payment_settings.mode", m)}
                    className={`px-4 py-1.5 text-sm capitalize ${(s.payment_settings?.mode || "test") === m ? "bg-blue-900 text-white" : "bg-white text-slate-600"}`}
                    data-testid={`payment-mode-${m}`}>{m === "test" ? t("pay.test") : t("pay.live")}</button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{t("pay.currency")}</Label>
                <Input value={s.payment_settings?.currency || s.currency || "EUR"} onChange={(e) => setField("payment_settings.currency", e.target.value)} data-testid="payment-currency" /></div>
              <div className="space-y-1.5"><Label>{t("pay.stripeSecret")}</Label>
                <Input type="password" placeholder="sk_..." value={s.payment_settings?.stripe_secret_key || ""} onChange={(e) => setField("payment_settings.stripe_secret_key", e.target.value)} data-testid="payment-stripe-secret" /></div>
              <div className="space-y-1.5"><Label>{t("pay.stripePublic")}</Label>
                <Input placeholder="pk_..." value={s.payment_settings?.stripe_publishable_key || ""} onChange={(e) => setField("payment_settings.stripe_publishable_key", e.target.value)} data-testid="payment-stripe-public" /></div>
            </div>
            <p className="text-xs text-slate-400 mt-2">{t("pay.envFallback")}</p>
            <div className="mt-6 pt-5 border-t border-slate-100">
              <Label className="font-medium">Onboarding account cliente/autista</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                {[["password_setup_enabled", "Link imposta password"], ["magic_login_enabled", "Magic login (un click)"]].map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`onboarding-${key}`}>
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                    <Switch checked={s.onboarding?.[key] !== false} onCheckedChange={(v) => {
                      const n = structuredClone(s); (n.onboarding ||= {}); n.onboarding[key] = v; setS(n);
                    }} data-testid={`onboarding-switch-${key}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <Label className="font-medium">{t("pay.methods")}</Label>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                {[["stripe", t("pay.stripe")], ["bank", t("pay.bank")], ["cash", t("pay.cash")], ["paypal", t("pay.paypal")]].map(([key, label]) => {
                  const def = key === "stripe";
                  const val = s.payment_settings?.methods?.[key];
                  return (
                    <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`payment-method-${key}`}>
                      <span className="text-sm font-medium text-slate-700">{label}</span>
                      <Switch checked={val === undefined ? def : !!val} onCheckedChange={(v) => {
                        const n = structuredClone(s);
                        (n.payment_settings ||= {});
                        (n.payment_settings.methods ||= {});
                        n.payment_settings.methods[key] = v; setS(n);
                      }} data-testid={`payment-method-switch-${key}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="seo">
          <Card title={t("set.c.seo")}>
            <p className="text-sm text-slate-500 mb-4">Metadati applicati alla homepage pubblica. Sitemap multilingua disponibile su <code className="text-xs">/api/sitemap.xml</code>, robots su <code className="text-xs">/api/robots.txt</code>.</p>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Titolo (title)</Label>
                <Input value={s.seo?.title || ""} onChange={(e) => setField("seo.title", e.target.value)} data-testid="seo-title" /></div>
              <div className="space-y-1.5"><Label>Descrizione (meta description)</Label>
                <Textarea rows={2} value={s.seo?.description || ""} onChange={(e) => setField("seo.description", e.target.value)} data-testid="seo-description" /></div>
              <div className="space-y-1.5"><Label>Parole chiave (keywords)</Label>
                <Input value={s.seo?.keywords || ""} onChange={(e) => setField("seo.keywords", e.target.value)} data-testid="seo-keywords" /></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>OG Title</Label>
                  <Input value={s.seo?.og_title || ""} onChange={(e) => setField("seo.og_title", e.target.value)} data-testid="seo-og-title" /></div>
                <div className="space-y-1.5"><Label>OG Image URL</Label>
                  <Input value={s.seo?.og_image || ""} onChange={(e) => setField("seo.og_image", e.target.value)} data-testid="seo-og-image" /></div>
              </div>
              <div className="space-y-1.5"><Label>OG Description</Label>
                <Textarea rows={2} value={s.seo?.og_description || ""} onChange={(e) => setField("seo.og_description", e.target.value)} data-testid="seo-og-description" /></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="legal">
          <Card title={t("set.c.legal")}>
            <div className="space-y-6">
              {Object.entries(s.legal_texts || {}).map(([key, val]) => (
                <div key={key} className="space-y-2">
                  <Label className="capitalize font-medium">{key}</Label>
                  {["it", "ro", "en"].map((loc) => (
                    <Textarea key={loc} rows={2} placeholder={loc.toUpperCase()} value={val?.[loc] || ""} onChange={(e) => { const n = structuredClone(s); n.legal_texts[key][loc] = e.target.value; setS(n); }} />
                  ))}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="flags">
          <Card title={t("set.c.flags")}>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(s.feature_flags || {}).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`flag-${key}`}>
                  <span className="capitalize text-sm font-medium text-slate-700">{key.replace(/_/g, " ")}</span>
                  <Switch checked={!!val} onCheckedChange={(v) => { const n = structuredClone(s); n.feature_flags[key] = v; setS(n); }} />
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
        {action && <Button size="sm" variant="outline" onClick={action}><Plus size={14} className="mr-1" /> Aggiungi</Button>}
      </div>
      {children}
    </div>
  );
}
